import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CampText from '../CampText';

/**
 * Passa 4 — les fites crítiques, una per una.
 *
 * Interrogar una fita que controles i una que no és la mateixa feina amb
 * preguntes diferents. Per això és **una sola pantalla i el formulari canvia
 * sol** segons la puntuació de control, en comptes de dues pantalles bessones.
 *
 * **Només hi entren les fites crítiques**, que és el que fa l'exercici
 * acabable: si una línia de temps de vint fites demanés vuit camps a cadascuna,
 * no s'arribaria mai al pla d'acció.
 *
 * El tercer estat —crítica però encara **sense puntuar**— no mostra cap dels
 * dos formularis. Ensenyar-ne un per defecte voldria dir triar per l'usuari una
 * cosa que encara no ha decidit, i les vuit preguntes de la branca controlada
 * no tenen sentit si la fita resulta que no depèn d'ell.
 */

const CAMPS_CONTROLADA = [
  'impact', 'who_gains_loses', 'what_gains_loses', 'who_feels_loss',
  'certainty', 'enablers', 'barriers', 'immediate_steps'
];
const CAMPS_NO_CONTROLADA = ['who_controls', 'impact_if_absent', 'influence_levers'];

const NIVELLS = ['full', 'partial', 'none'];

const teResposta = (fita) => {
  const camps = fita.control_level === 'none' ? CAMPS_NO_CONTROLADA : CAMPS_CONTROLADA;
  return camps.some(c => (fita[c] || '').trim());
};

const AnalysisStep = ({ milestones, onPatch }) => {
  const { t } = useTranslation();

  const critiques = [...milestones]
    .filter(m => m.is_critical)
    .sort((a, b) => (a.position - b.position) || (a.id - b.id));

  const [triada, setTriada] = useState(null);

  // Si la fita oberta deixa de ser crítica o s'esborra des d'una altra
  // pestanya, cau a la primera que quedi en comptes de deixar la pantalla en
  // blanc sense dir per què.
  useEffect(() => {
    if (!critiques.length) { setTriada(null); return; }
    if (!critiques.some(m => m.id === triada)) setTriada(critiques[0].id);
  }, [critiques, triada]);

  const fita = critiques.find(m => m.id === triada);

  if (!critiques.length) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-neutral-900">{t('backcasting.step4Title')}</h3>
          <p className="mt-1 max-w-3xl text-sm text-neutral-600">{t('backcasting.step4Lead')}</p>
        </div>
        <p className="rounded-lg border border-dashed border-neutral-300 px-4 py-10 text-center text-sm text-neutral-500">
          {t('backcasting.noCriticalMilestones')}
        </p>
      </div>
    );
  }

  const camps = fita && fita.control_level === 'none' ? CAMPS_NO_CONTROLADA : CAMPS_CONTROLADA;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-neutral-900">{t('backcasting.step4Title')}</h3>
        <p className="mt-1 max-w-3xl text-sm text-neutral-600">
          {t('backcasting.step4Count', {
            count: critiques.length,
            analysed: critiques.filter(teResposta).length
          })}
        </p>
      </div>

      {/* Cua de feina: deixa saltar entre fites en qualsevol ordre, que és el
          que permet repartir-se-les entre dues persones. */}
      <div className="flex flex-wrap gap-2">
        {critiques.map(m => {
          const activa = m.id === triada;
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => setTriada(m.id)}
              aria-pressed={activa}
              className={`max-w-xs truncate rounded-md border px-3 py-1.5 text-xs transition-colors ${
                activa
                  ? 'border-accent-300 bg-accent-50 font-semibold text-accent-700'
                  : 'border-neutral-200 bg-neutral-50 text-neutral-600 hover:border-neutral-300'
              }`}
              title={m.title}
            >
              {teResposta(m) && <span className="mr-1 text-neutral-400" aria-hidden="true">✓</span>}
              {m.title || t('backcasting.untitledMilestone')}
              {m.period_label ? ` · ${m.period_label}` : ''}
            </button>
          );
        })}
      </div>

      {fita && (
        <div className="overflow-hidden rounded-lg border border-neutral-200">
          <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-neutral-200 bg-neutral-50 px-5 py-3">
            <h4 className="text-sm font-semibold text-neutral-900">
              {fita.title || t('backcasting.untitledMilestone')}
            </h4>
            <span className="rounded border border-neutral-300 px-2 py-0.5 text-xs font-medium text-neutral-600">
              {fita.period_label ? `${fita.period_label} · ` : ''}
              {fita.control_level
                ? t(`backcasting.control.${fita.control_level}`)
                : t('backcasting.unscored')}
            </span>
          </div>

          <div className="p-5">
            {!fita.control_level ? (
              /* Crítica però sense puntuar: no hi ha formulari, hi ha la
                 pregunta que falta. Es pot respondre aquí mateix sense tornar
                 a la línia de temps. */
              <div className="rounded-lg border border-dashed border-neutral-300 px-4 py-6 text-center">
                <p className="text-sm text-neutral-600">{t('backcasting.scoreControlFirst')}</p>
                <div className="mt-4 flex flex-wrap justify-center gap-2">
                  {NIVELLS.map(n => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => onPatch(fita.id, { control_level: n })}
                      className="rounded-full border border-neutral-300 bg-white px-3.5 py-1.5 text-xs font-medium text-neutral-700 hover:border-neutral-400 hover:bg-neutral-50"
                    >
                      {t(`backcasting.control.${n}`)}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className={camps.length > 4 ? 'grid gap-5 lg:grid-cols-2' : 'space-y-5'}>
                {camps.map(camp => (
                  <CampText
                    key={camp}
                    label={t(`backcasting.field.${camp}`)}
                    value={fita[camp]}
                    onSave={(v) => onPatch(fita.id, { [camp]: v })}
                    multiline
                    rows={3}
                    inputClassName="text-sm leading-relaxed"
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalysisStep;
